import { supabase } from './supabase';
import type {
  PortalUser,
  PortalUserInsert,
  PortalUserUpdate,
  PortalUserFull,
  Role,
  RoleName,
  UserCustomerAccess,
  UserCustomerAccessInsert,
  UserCustomerAccessUpdate,
  UserCustomerAccessWithDetails,
  UserSectionPermission,
  UserSectionPermissionInsert,
  SectionName,
} from '../types/permissions';
import { ALL_SECTIONS } from '../types/permissions';

// ============================================
// PORTAL USERS API
// ============================================
export const usersApi = {
  async getAll(): Promise<PortalUser[]> {
    const { data, error } = await supabase
      .from('portal_users')
      .select('*')
      .order('email');

    if (error) throw error;
    return data || [];
  },

  async getByClerkId(clerkUserId: string): Promise<PortalUser | null> {
    const { data, error } = await supabase
      .from('portal_users')
      .select('*')
      .eq('clerk_user_id', clerkUserId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  async getById(id: string): Promise<PortalUser | null> {
    const { data, error } = await supabase
      .from('portal_users')
      .select('*')
      .eq('id', id)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  async getFullByClerkId(clerkUserId: string): Promise<PortalUserFull | null> {
    // First get the portal user
    const portalUser = await usersApi.getByClerkId(clerkUserId);
    if (!portalUser) return null;

    // Get roles
    const { data: userRoles, error: rolesError } = await supabase
      .from('user_roles')
      .select(`
        role_id,
        roles (*)
      `)
      .eq('user_id', portalUser.id);

    if (rolesError) throw rolesError;

    const roles: Role[] = (userRoles || [])
      .map((ur: any) => ur.roles)
      .filter(Boolean);

    const highestRoleLevel = roles.length > 0
      ? Math.min(...roles.map(r => r.hierarchy_level))
      : 999;

    // Get customer access with section permissions
    const { data: accessData, error: accessError } = await supabase
      .from('user_customer_access')
      .select(`
        *,
        customers!inner (name)
      `)
      .eq('user_id', portalUser.id);

    if (accessError) throw accessError;

    // Get section permissions for each access
    const customerAccess: UserCustomerAccessWithDetails[] = [];
    for (const access of accessData || []) {
      const { data: sectionPerms, error: sectionError } = await supabase
        .from('user_section_permissions')
        .select('*')
        .eq('user_customer_access_id', access.id);

      if (sectionError) throw sectionError;

      customerAccess.push({
        ...access,
        customer_name: access.customers?.name || '',
        section_permissions: sectionPerms || [],
      });
    }

    return {
      ...portalUser,
      roles,
      highest_role_level: highestRoleLevel,
      customer_access: customerAccess,
    };
  },

  async create(user: PortalUserInsert): Promise<PortalUser> {
    const { data, error } = await supabase
      .from('portal_users')
      .insert(user)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: PortalUserUpdate): Promise<PortalUser> {
    const { data, error } = await supabase
      .from('portal_users')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('portal_users')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async syncFromClerk(
    clerkUserId: string,
    email: string,
    firstName?: string | null,
    lastName?: string | null
  ): Promise<PortalUser> {
    const existing = await usersApi.getByClerkId(clerkUserId);

    if (existing) {
      // Update existing user
      return await usersApi.update(existing.id, {
        email,
        first_name: firstName,
        last_name: lastName,
      });
    }

    // Create new user
    return await usersApi.create({
      clerk_user_id: clerkUserId,
      email,
      first_name: firstName,
      last_name: lastName,
      is_active: true,
    });
  },
};

// ============================================
// ROLES API
// ============================================
export const rolesApi = {
  async getAll(): Promise<Role[]> {
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .order('hierarchy_level');

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Role | null> {
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .eq('id', id)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  async getByName(name: RoleName): Promise<Role | null> {
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .eq('name', name)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  async getUserRoles(userId: string): Promise<Role[]> {
    const { data, error } = await supabase
      .from('user_roles')
      .select(`
        roles (*)
      `)
      .eq('user_id', userId);

    if (error) throw error;
    return (data || []).map((ur: any) => ur.roles).filter(Boolean);
  },

  async assignRole(userId: string, roleId: string): Promise<void> {
    const { error } = await supabase
      .from('user_roles')
      .insert({ user_id: userId, role_id: roleId });

    if (error && error.code !== '23505') throw error; // Ignore duplicate key
  },

  async removeRole(userId: string, roleId: string): Promise<void> {
    const { error } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role_id', roleId);

    if (error) throw error;
  },

  async setUserRole(userId: string, roleName: RoleName): Promise<void> {
    // Get the role by name
    const role = await rolesApi.getByName(roleName);
    if (!role) throw new Error(`Role not found: ${roleName}`);

    // Remove all existing roles
    const { error: deleteError } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId);

    if (deleteError) throw deleteError;

    // Assign the new role
    await rolesApi.assignRole(userId, role.id);
  },
};

// ============================================
// CUSTOMER ACCESS API
// ============================================
export const customerAccessApi = {
  async getByUser(userId: string): Promise<UserCustomerAccessWithDetails[]> {
    const { data, error } = await supabase
      .from('user_customer_access')
      .select(`
        *,
        customers!inner (name)
      `)
      .eq('user_id', userId);

    if (error) throw error;

    const results: UserCustomerAccessWithDetails[] = [];
    for (const access of data || []) {
      const { data: sectionPerms } = await supabase
        .from('user_section_permissions')
        .select('*')
        .eq('user_customer_access_id', access.id);

      results.push({
        ...access,
        customer_name: access.customers?.name || '',
        section_permissions: sectionPerms || [],
      });
    }

    return results;
  },

  async getByCustomer(customerId: string): Promise<UserCustomerAccess[]> {
    const { data, error } = await supabase
      .from('user_customer_access')
      .select('*')
      .eq('customer_id', customerId);

    if (error) throw error;
    return data || [];
  },

  async create(access: UserCustomerAccessInsert): Promise<UserCustomerAccess> {
    const { data, error } = await supabase
      .from('user_customer_access')
      .insert(access)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: UserCustomerAccessUpdate): Promise<UserCustomerAccess> {
    const { data, error } = await supabase
      .from('user_customer_access')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('user_customer_access')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async grantAccess(
    userId: string,
    customerId: string,
    permissions: { canView?: boolean; canEdit?: boolean; canDelete?: boolean } = {}
  ): Promise<UserCustomerAccess> {
    const { canView = true, canEdit = false, canDelete = false } = permissions;

    // Check if access already exists
    const { data: existing } = await supabase
      .from('user_customer_access')
      .select('id')
      .eq('user_id', userId)
      .eq('customer_id', customerId)
      .single();

    if (existing) {
      return await customerAccessApi.update(existing.id, {
        can_view: canView,
        can_edit: canEdit,
        can_delete: canDelete,
      });
    }

    return await customerAccessApi.create({
      user_id: userId,
      customer_id: customerId,
      can_view: canView,
      can_edit: canEdit,
      can_delete: canDelete,
    });
  },

  async revokeAccess(userId: string, customerId: string): Promise<void> {
    const { error } = await supabase
      .from('user_customer_access')
      .delete()
      .eq('user_id', userId)
      .eq('customer_id', customerId);

    if (error) throw error;
  },
};

// ============================================
// SECTION PERMISSIONS API
// ============================================
export const sectionPermissionsApi = {
  async getByAccess(accessId: string): Promise<UserSectionPermission[]> {
    const { data, error } = await supabase
      .from('user_section_permissions')
      .select('*')
      .eq('user_customer_access_id', accessId);

    if (error) throw error;
    return data || [];
  },

  async create(permission: UserSectionPermissionInsert): Promise<UserSectionPermission> {
    const { data, error } = await supabase
      .from('user_section_permissions')
      .insert(permission)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: { can_view?: boolean; can_edit?: boolean }): Promise<UserSectionPermission> {
    const { data, error } = await supabase
      .from('user_section_permissions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('user_section_permissions')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async setPermission(
    accessId: string,
    sectionName: SectionName,
    canView: boolean,
    canEdit: boolean
  ): Promise<UserSectionPermission> {
    // Check if permission exists
    const { data: existing } = await supabase
      .from('user_section_permissions')
      .select('id')
      .eq('user_customer_access_id', accessId)
      .eq('section_name', sectionName)
      .single();

    if (existing) {
      return await sectionPermissionsApi.update(existing.id, {
        can_view: canView,
        can_edit: canEdit,
      });
    }

    return await sectionPermissionsApi.create({
      user_customer_access_id: accessId,
      section_name: sectionName,
      can_view: canView,
      can_edit: canEdit,
    });
  },

  async setAllPermissions(
    accessId: string,
    permissions: Record<SectionName, { canView: boolean; canEdit: boolean }>
  ): Promise<void> {
    for (const section of ALL_SECTIONS) {
      const perm = permissions[section];
      if (perm) {
        await sectionPermissionsApi.setPermission(accessId, section, perm.canView, perm.canEdit);
      }
    }
  },

  async createDefaultPermissions(accessId: string, canEdit: boolean = false): Promise<void> {
    for (const section of ALL_SECTIONS) {
      await sectionPermissionsApi.setPermission(accessId, section, true, canEdit);
    }
  },
};

// ============================================
// PERMISSION HELPERS
// ============================================
export const permissionsApi = {
  async getUserWithPermissions(clerkUserId: string): Promise<PortalUserFull | null> {
    return await usersApi.getFullByClerkId(clerkUserId);
  },

  async getAllUsersWithRoles(): Promise<PortalUserFull[]> {
    const users = await usersApi.getAll();
    const results: PortalUserFull[] = [];

    for (const user of users) {
      const full = await usersApi.getFullByClerkId(user.clerk_user_id);
      if (full) results.push(full);
    }

    return results;
  },

  async grantFullCustomerAccess(userId: string, customerId: string): Promise<void> {
    const access = await customerAccessApi.grantAccess(userId, customerId, {
      canView: true,
      canEdit: true,
      canDelete: true,
    });
    await sectionPermissionsApi.createDefaultPermissions(access.id, true);
  },

  async grantReadOnlyCustomerAccess(userId: string, customerId: string): Promise<void> {
    const access = await customerAccessApi.grantAccess(userId, customerId, {
      canView: true,
      canEdit: false,
      canDelete: false,
    });
    await sectionPermissionsApi.createDefaultPermissions(access.id, false);
  },
};
